import { h } from '../lib/dom.js';
import { getState } from '../store.js';
import { iconMarkup } from '../icons/index.js';
import { t } from '../i18n/index.js';
import {
  setLockCode, unlock, unlockWithWebauthn,
  openLandingForm, setNewProfileName, setNewProfileEmail, toggleNewProfileAnonymous,
  setNewProfileCode, setNewProfileCodeConfirm, validateNewProfileEmailBlur,
  validateNewProfileCodeBlur, validateNewProfileCodeConfirmBlur,
  toggleAgreeTerms, toggleAgreeSelfCustody, submitOpenAccount, openLicense, cancelAddProfile,
  openLanguagePicker
} from '../actions.js';

function logo(sizeClass) {
  return h('img', { class: 'logo ' + sizeClass, src: '/icons/logo.svg', alt: 'Tynti' });
}

// Top-right on both pre-auth entry screens (new-account intro and
// welcome-back) — lets a user land in their own language up front, via
// the same setLanguage() Settings' own select calls, instead of only
// discovering the Settings > Language control after unlocking.
function languagePickerButton() {
  return h('button', {
    class: 'icon-btn-plain lang-picker-btn', 'aria-label': t('landing.chooseLanguage'), onclick: openLanguagePicker
  }, h('span', { html: iconMarkup('languages', { size: 18 }) }));
}

// A real, focusable <input type="checkbox"> stays in the DOM (visually
// hidden via CSS, not removed) so keyboard/screen-reader behavior is
// unchanged — only its visible appearance is swapped for the lucide
// square/square-check icons. The icon is purely decorative (aria-hidden,
// pointer-events:none): clicking it still hits the underlying input
// because both sit inside the same <label>, which natively toggles its
// nested control on any click.
function customCheckbox(checked, onchange) {
  return h('span', { class: 'custom-checkbox-wrap' }, [
    h('input', { type: 'checkbox', class: 'custom-checkbox-input', checked, onchange }),
    h('span', { class: 'custom-checkbox-icon', 'aria-hidden': 'true', html: iconMarkup(checked ? 'squareCheck' : 'square', { size: 20, color: checked ? '#4D4DFF' : '#fff' }) })
  ]);
}

// Only shown when adding a profile from an already-unlocked Home session —
// a brand-new device has nowhere else to go, but this entry point does.
function backButton(s) {
  if (!s.landingAddingProfile) return null;
  return h('button', { class: 'icon-btn icon-back landing-back', onclick: cancelAddProfile }, [
    h('span', { html: iconMarkup('arrowLeft', { size: 14 }) }), ' ' + t('landing.back')
  ]);
}

// A bold "label" followed by plain continuation text — the recurring
// pattern the source FAQ doc uses for every bullet ("**Faster:** ...",
// "**Owner supremacy** — ..."). One small composer instead of splitting
// each bullet's markup out by hand, still zero markdown parsing — see
// DECISIONS.md (2026-08-11) for why a parser was deliberately not built.
function faqBullet(label, rest) {
  return h('li', {}, [h('strong', {}, label), rest]);
}

function faqItem(titleKey, answer) {
  return h('details', { class: 'faq-item' }, [
    h('summary', {}, t(titleKey)),
    h('div', { class: 'faq-answer' }, answer)
  ]);
}

// External citation links (standards bodies, regulatory text) — hrefs are
// constants, not translated strings; only the visible label comes from
// i18n. Same target/rel convention as the existing block-explorer links
// (activity.js/send.js/transfer.js's viewOnChain).
function externalLink(href, label) {
  return h('a', { href, target: '_blank', rel: 'noopener noreferrer' }, label);
}

// Real, currently-live URLs for the standards named in faqStandards*/
// faqRegulated* (verified via WebFetch before use, 2026-08-11) — not
// reproducing their bullet-by-bullet requirements here, see the comment on
// faqStandardsTitle in en.js for why. No TRC-20 link: Tron's own docs
// don't have one stable enough to be confident citing from memory: it's
// named as plain text instead (faqStandardsBullet3After) rather than guessed.
const STANDARD_HREFS = {
  asvs: 'https://owasp.org/www-project-application-security-verification-standard/',
  ccss: 'https://cryptoconsortium.org/',
  bip: 'https://github.com/bitcoin/bips/blob/master/bip-0039.mediawiki',
  eip55: 'https://eips.ethereum.org/EIPS/eip-55',
  esma: 'https://www.esma.europa.eu/esmas-activities/digital-finance-and-innovation/markets-crypto-assets-regulation-mica',
  mhc: 'https://www.mhc.ie/hubs/legislation/the-markets-in-crypto-assets-regulation'
};

function standardBullet(labelKey, textKey, linkLabelKey, href) {
  return h('li', {}, [
    h('strong', {}, t(labelKey) + ': '),
    t(textKey) + ' ',
    externalLink(href, t(linkLabelKey))
  ]);
}

function faq() {
  return h('div', { class: 'faq stack mt-md' }, [
    h('div', { class: 'faq-intro' }, t('landing.faqIntro')),

    h('div', { class: 'faq-section-title' }, t('landing.faqSectionOverview')),
    faqItem('landing.faqWhatIsTitle', t('landing.faqWhatIsAnswer')),

    h('div', { class: 'faq-section-title' }, t('landing.faqSectionControls')),
    // Hidden 2026-08-13: describes multi-user roles, maker-checker approval,
    // and multi-sig (2-of-2/2-of-3) — none of this exists in this version.
    // The app is single-profile-per-device with one passcode; there's no
    // staff/clerk/view-only role model or multi-signer flow to point to.
    // Strings kept in en.js (faqStaff*) in case this ships later — remove
    // this comment block along with them if it's decided not to.
    // faqItem('landing.faqStaffTitle', [
    //   h('p', {}, t('landing.faqStaffLead')),
    //   h('ul', { class: 'faq-list' }, [
    //     faqBullet(t('landing.faqStaffBullet1Label'), t('landing.faqStaffBullet1Text')),
    //     faqBullet(t('landing.faqStaffBullet2Label'), t('landing.faqStaffBullet2Text')),
    //     faqBullet(t('landing.faqStaffBullet3Label'), t('landing.faqStaffBullet3Text')),
    //     faqBullet(t('landing.faqStaffBullet4Label'), t('landing.faqStaffBullet4Text')),
    //     faqBullet(t('landing.faqStaffBullet5Label'), t('landing.faqStaffBullet5Text'))
    //   ])
    // ]),
    faqItem('landing.faqAddressTitle', t('landing.faqAddressAnswer')),
    faqItem('landing.faqSeesTitle', t('landing.faqSeesAnswer')),
    faqItem('landing.faqMistakeTitle', [
      h('p', {}, [t('landing.faqMistakeIntroPre'), h('strong', {}, t('landing.faqMistakeIntroBold')), t('landing.faqMistakeIntroPost')]),
      h('p', {}, t('landing.faqMistakeListLead')),
      h('ul', { class: 'faq-list' }, [
        faqBullet(t('landing.faqMistakeBullet1Label'), t('landing.faqMistakeBullet1Text')),
        faqBullet(t('landing.faqMistakeBullet2Label'), t('landing.faqMistakeBullet2Text')),
        faqBullet(t('landing.faqMistakeBullet3Label'), t('landing.faqMistakeBullet3Text'))
      ]),
      h('p', {}, t('landing.faqMistakeClosing'))
    ]),
    faqItem('landing.faqDisappearTitle', t('landing.faqDisappearAnswer')),

    h('div', { class: 'faq-section-title' }, t('landing.faqSectionCompliance')),
    // Hidden 2026-08-13: "Evidence Pack" claims a one-click PDF dossier with
    // an *attached commercial invoice* and counterparty profile — no invoice
    // attachment/upload exists anywhere in the app. What's real (a CSV/PDF
    // transaction statement export) is already covered by faqExportTitle
    // below, so this isn't just a wording fix, the described artifact
    // doesn't exist. String kept in en.js (faqEvidence*).
    // faqItem('landing.faqEvidenceTitle', t('landing.faqEvidenceAnswer')),
    // Hidden 2026-08-13: describes payment-request links with invoice
    // references and automatic inbound-transfer matching — payment
    // requests/links were removed from this app entirely (see git history,
    // "remove: payment request / payment link functionality"). No invoice
    // linking or reconciliation exists. String kept in en.js (faqInvoice*).
    // faqItem('landing.faqInvoiceTitle', t('landing.faqInvoiceAnswer')),
    faqItem('landing.faqSegregateTitle', t('landing.faqSegregateAnswer')),
    // Hidden 2026-08-13: leads with "payment request links" and "address
    // cards" — payment requests/links were removed (see above); no
    // dedicated shareable "address card" or WhatsApp-formatted receipt
    // artifact exists (Receive's QR/address can be shared, but not as this
    // described feature). String kept in en.js (faqWhatsApp*).
    // faqItem('landing.faqWhatsAppTitle', t('landing.faqWhatsAppAnswer')),
    faqItem('landing.faqExportTitle', t('landing.faqExportAnswer')),

    h('div', { class: 'faq-section-title' }, t('landing.faqSectionTrust')),
    faqItem('landing.faqRegulatedTitle', [
      h('p', {}, [
        t('landing.faqRegulatedAnswerPre'), ' ',
        externalLink(STANDARD_HREFS.esma, '[1]'), ' ',
        externalLink(STANDARD_HREFS.mhc, '[2]')
      ]),
      h('p', {}, t('landing.faqRegulatedAnswerPost'))
    ]),
    faqItem('landing.faqKycTitle', t('landing.faqKycAnswer')),
    faqItem('landing.faqFreezeTitle', t('landing.faqFreezeAnswer')),
    faqItem('landing.faqSafeTitle', t('landing.faqSafeAnswer')),

    h('div', { class: 'faq-section-title' }, t('landing.faqSectionStandards')),
    faqItem('landing.faqStandardsTitle', [
      h('p', {}, t('landing.faqStandardsIntro')),
      h('ul', { class: 'faq-list' }, [
        standardBullet('landing.faqStandardsBullet1Label', 'landing.faqStandardsBullet1Text', 'landing.faqStandardsBullet1Link', STANDARD_HREFS.asvs),
        standardBullet('landing.faqStandardsBullet2Label', 'landing.faqStandardsBullet2Text', 'landing.faqStandardsBullet2Link', STANDARD_HREFS.ccss),
        h('li', {}, [
          h('strong', {}, t('landing.faqStandardsBullet3Label') + ': '),
          t('landing.faqStandardsBullet3Text') + ' ',
          externalLink(STANDARD_HREFS.bip, t('landing.faqStandardsBullet3Link1')), ', ',
          externalLink(STANDARD_HREFS.eip55, t('landing.faqStandardsBullet3Link2')),
          t('landing.faqStandardsBullet3After')
        ]),
        faqBullet(t('landing.faqStandardsBullet4Label') + ': ', t('landing.faqStandardsBullet4Text'))
      ])
    ]),

    h('div', { class: 'faq-section-title' }, t('landing.faqSectionBackground')),
    faqItem('landing.faqStablecoinTitle', t('landing.faqStablecoinAnswer')),
    faqItem('landing.faqDifferentTitle', [
      h('p', {}, [t('landing.faqDifferentLeadPre'), h('strong', {}, t('landing.faqDifferentLeadBold'))]),
      h('p', {}, t('landing.faqDifferentBody1')),
      h('p', {}, t('landing.faqDifferentBody2'))
    ]),
    faqItem('landing.faqWhyTitle', [
      h('ul', { class: 'faq-list' }, [
        faqBullet(t('landing.faqWhyBullet1Label'), ' ' + t('landing.faqWhyBullet1Text')),
        faqBullet(t('landing.faqWhyBullet2Label'), ' ' + t('landing.faqWhyBullet2Text')),
        faqBullet(t('landing.faqWhyBullet3Label'), ' ' + t('landing.faqWhyBullet3Text'))
      ]),
      h('p', {}, t('landing.faqWhyNote'))
    ]),
    faqItem('landing.faqFeesTitle', t('landing.faqFeesAnswer')),

    h('div', { class: 'faq-section-title' }, t('landing.faqSectionTechnical')),
    faqItem('landing.faqAppTitle', t('landing.faqAppAnswer')),
    faqItem('landing.faqFiatTitle', t('landing.faqFiatAnswer'))
  ]);
}

function renderWelcomeBack(s) {
  const profile = s.profiles.find((p) => p.id === s.activeProfileId) || s.profiles[0];
  const input = h('input', {
    class: 'lock-input',
    value: s.lockCode,
    placeholder: t('landing.passcodePlaceholder'),
    maxlength: '6',
    inputmode: 'numeric',
    oninput: (e) => setLockCode(e.target.value)
  });
  input.addEventListener('keydown', (e) => { if (e.key === 'Enter') unlock(); });

  // Biometric unlock only shows when this device *and* this specific
  // profile both support it — the button is a convenience layer over the
  // same vault the PIN below unwraps (see vault.js), never a replacement
  // for it, so the PIN field is always present regardless.
  const canWebauthn = s.webauthnAvailable && profile && profile.vault && profile.vault.webauthn;

  // feature-harden.md 6.1: disable the PIN unlock button itself with a
  // visible countdown during an active lockout, not just a static error
  // message — main.js ticks a render once/sec while this is active so the
  // countdown actually decrements live rather than sitting frozen at
  // whatever number the last failed attempt computed. Biometric unlock
  // (canWebauthn button above) is deliberately NOT gated by this — see
  // DECISIONS.md for why the throttle targets the guessable low-entropy
  // PIN path only.
  const sec = (profile && profile.security) || { lockoutUntil: null };
  const lockedOutMs = sec.lockoutUntil ? sec.lockoutUntil - Date.now() : 0;
  const lockedOut = lockedOutMs > 0;
  const lockoutSeconds = lockedOut ? Math.ceil(lockedOutMs / 1000) : 0;

  // dark-screen-scroll, not plain dark-screen: the FAQ below makes this
  // screen taller than one viewport on mobile, and dark-screen's fixed
  // centering would clip it (same reasoning as the new-account form —
  // see the comment on .dark-screen-scroll in style.css).
  return h('div', { class: 'dark-screen dark-screen-scroll' }, [
    languagePickerButton(),
    logo('logo-sm'),
    h('div', { class: 'label' }, profile ? t('landing.welcomeBackName', { name: profile.name }) : t('landing.welcomeBack')),
    canWebauthn ? h('button', {
      class: 'btn btn-primary btn-lg', disabled: s.lockChecking, onclick: unlockWithWebauthn
    }, [
      h('span', { html: iconMarkup('scanFace', { size: 18 }) }), ' ', s.lockChecking ? t('common.checking') : t('landing.unlockWithBiometrics')
    ]) : null,
    h('div', { class: 'copy' }, canWebauthn ? t('landing.orEnterPasscode') : t('landing.enterPasscode')),
    input,
    h('div', { class: 'lock-error' }, lockedOut ? t('errors.tooManyAttempts', { seconds: lockoutSeconds }) : (s.lockError || '')),
    h('button', {
      class: 'btn btn-lg ' + (canWebauthn ? 'btn-secondary' : 'btn-primary'),
      disabled: s.lockChecking || lockedOut, onclick: unlock
    }, s.lockChecking ? t('common.checking') : lockedOut ? t('landing.unlockLockedOut', { seconds: lockoutSeconds }) : t('landing.unlock')),
    faq()
  ]);
}

function renderNewAccountIntro(s) {
  return h('div', { class: 'dark-screen' }, [
    languagePickerButton(),
    backButton(s),
    logo('logo-lg'),
    h('div', { class: 'copy' }, t('landing.heroCopy')),
    h('button', { class: 'btn btn-primary btn-lg', onclick: openLandingForm }, t('landing.instantAccountOpening')),
    faq()
  ]);
}

function renderNewAccountForm(s) {
  const errors = s.newProfileErrors;

  const nameInput = h('input', {
    placeholder: t('landing.namePlaceholder'),
    value: s.newProfileName,
    disabled: s.newProfileAnonymous,
    oninput: (e) => setNewProfileName(e.target.value)
  });
  const emailInput = h('input', {
    type: 'email',
    placeholder: t('landing.emailPlaceholder'),
    value: s.newProfileEmail,
    disabled: s.newProfileAnonymous,
    oninput: (e) => setNewProfileEmail(e.target.value),
    onblur: validateNewProfileEmailBlur
  });
  const anonRow = h('label', { class: 'checkbox-row' }, [
    customCheckbox(s.newProfileAnonymous, toggleNewProfileAnonymous),
    h('span', {}, t('landing.anonymous'))
  ]);

  const codeInput = h('input', {
    class: 'code-input',
    placeholder: t('landing.codePlaceholder'),
    maxlength: '6',
    inputmode: 'numeric',
    value: s.newProfileCode,
    oninput: (e) => setNewProfileCode(e.target.value),
    onblur: validateNewProfileCodeBlur
  });
  const codeConfirmInput = h('input', {
    class: 'code-input',
    placeholder: t('landing.codeConfirmPlaceholder'),
    maxlength: '6',
    inputmode: 'numeric',
    value: s.newProfileCodeConfirm,
    oninput: (e) => setNewProfileCodeConfirm(e.target.value),
    onblur: validateNewProfileCodeConfirmBlur
  });

  const termsRow = h('label', { class: 'checkbox-row' }, [
    customCheckbox(s.agreeTerms, toggleAgreeTerms),
    h('span', {}, [
      t('landing.agreeTermsPre'),
      h('a', { href: '#', onclick: (e) => { e.preventDefault(); openLicense(); } }, t('landing.agreeTermsLink')),
      t('landing.agreeTermsPost')
    ])
  ]);
  const selfCustodyRow = h('label', { class: 'checkbox-row' }, [
    customCheckbox(s.agreeSelfCustody, toggleAgreeSelfCustody),
    h('span', {}, [
      t('landing.agreeSelfCustodyPre'),
      h('a', { href: '#', onclick: (e) => { e.preventDefault(); openLicense(); } }, t('landing.agreeSelfCustodyLink')),
      t('landing.agreeSelfCustodyPost')
    ])
  ]);

  return h('div', { class: 'dark-screen dark-screen-scroll' }, [
    backButton(s),
    logo('logo-sm'),
    h('div', { class: 'label' }, t('landing.openYourAccount')),
    h('div', { class: 'field stack' }, [nameInput, emailInput, anonRow]),
    h('div', { class: 'field-error' }, errors.name || errors.email || ''),
    h('div', { class: 'field stack' }, [codeInput, codeConfirmInput]),
    h('div', { class: 'field-error' }, errors.code || ''),
    h('div', { class: 'stack' }, [termsRow, selfCustodyRow]),
    h('div', { class: 'field-error' }, errors.agree || ''),
    h('button', {
      class: 'btn btn-primary btn-lg',
      disabled: s.newProfileSubmitting,
      onclick: s.newProfileSubmitting ? null : submitOpenAccount
    }, s.newProfileSubmitting ? t('landing.opening') : t('landing.openAccount')),
    faq()
  ]);
}

export function renderLanding() {
  const s = getState();
  if (s.profiles.length > 0 && !s.landingAddingProfile) return renderWelcomeBack(s);
  if (!s.landingFormOpen) return renderNewAccountIntro(s);
  return renderNewAccountForm(s);
}
